import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import { ENV } from "@/config/env";
import { rootStore } from "@/mobx/store";
import {
  refreshAccessToken,
  isTokenExpiringSoon,
} from "@/utils/tokenRefresh";
import qs from "qs";

// 글로벌 로딩 상태 관리를 위한 변수
let activeRequests = 0;
const loadingSubscribers = new Set<(loading: boolean) => void>();


// 로딩 상태 변경 알림 함수
const notifyLoadingChange = (loading: boolean) => {
  loadingSubscribers.forEach((callback) => callback(loading));
};

// 로딩 상태 구독 함수
export const subscribeToApiClientLoading = (
  callback: (loading: boolean) => void
) => {
  loadingSubscribers.add(callback);
  return () => loadingSubscribers.delete(callback);
};

// API 응답 타입 - 다양한 응답 구조를 지원
export interface ApiResponse<T = any> {
  result_code: number;
  result_msg: string;
  result_data?: T;
  content?: T;
  totalPages?: number;
  totalElements?: number;
  page?: number;
  size?: number;
  // 직접 데이터가 오는 경우를 위한 인덱스 시그니처
  [key: string]: any;
}

// 응답 데이터 정규화 함수
export function normalizeResponseData<T>(response: ApiResponse<T>): {
  data: T;
  pagination?: {
    totalPages?: number;
    totalElements?: number;
    page?: number;
    size?: number;
  };
} {
  let data: T;
  let pagination:
    | {
        totalPages?: number;
        totalElements?: number;
        page?: number;
        size?: number;
      }
    | undefined;

  // 1. result_data가 있는 경우
  if (response.result_data !== undefined) {
    data = response.result_data;
    // result_data 내부에 content가 있는 경우
    if (
      response.result_data &&
      typeof response.result_data === "object" &&
      "content" in response.result_data
    ) {
      data = (response.result_data as any).content;
      pagination = {
        totalPages: (response.result_data as any).totalPages,
        totalElements: (response.result_data as any).totalElements,
        page: (response.result_data as any).page,
        size: (response.result_data as any).size,
      };
    }
  }
  // 2. content가 있는 경우
  else if (response.content !== undefined) {
    data = response.content;
  }
  // 3. 직접 데이터가 오는 경우 (result_code, result_msg 제외한 나머지)
  else {
    const { result_code, result_msg, ...directData } = response;
    data = directData as T;
  }

  // 최상위 레벨에 pagination 정보가 있는 경우
  if (
    !pagination &&
    (response.totalPages !== undefined || response.totalElements !== undefined)
  ) {
    pagination = {
      totalPages: response.totalPages,
      totalElements: response.totalElements,
      page: response.page,
      size: response.size,
    };
  }

  return { data, pagination };
}

// 정규화된 응답 타입
export interface NormalizedApiResponse<T = any> {
  data: T;
  pagination?: {
    totalPages?: number;
    totalElements?: number;
    page?: number;
    size?: number;
  };
  result_code: number;
  result_msg: string;
}

// API 클라이언트 생성
export const apiClient: AxiosInstance = axios.create({
  baseURL: ENV.API_URL.replace(/\/$/, ""),
  timeout: 30000,
  headers: {
    "Content-Type": "application/json",
    "Accept-Language": "ko",
  },
  paramsSerializer: (params) => qs.stringify(params, { arrayFormat: "repeat" }), // 배열 파라미터 직렬화
});

// 요청 인터셉터
apiClient.interceptors.request.use(
  async (config) => {
    // 로딩 상태 증가
    activeRequests++;

    notifyLoadingChange(true);

    // FormData인 경우 Content-Type을 자동으로 설정하도록 제거
    if (config.data instanceof FormData) {
      delete config.headers["Content-Type"];
    }

    try {
      // refresh endpoint가 아닌 경우에만 토큰 갱신 시도
      if (!config.url?.includes("/auth/refresh")) {
        const refreshToken = localStorage.getItem("refresh_token");
        if (refreshToken) {
          const currentToken = rootStore.token;

          // 토큰이 없거나 곧 만료될 예정이면 갱신
          if (!currentToken || isTokenExpiringSoon(currentToken)) {
            try {
              const newToken = await refreshAccessToken();
              config.headers.Authorization = `Bearer ${newToken}`;
            } catch (refreshError) {
              console.error(
                "Token refresh failed in request interceptor:",
                refreshError
              );
              // 토큰 갱신 실패 시 기존 토큰 사용 (있는 경우)
              if (currentToken) {
                config.headers.Authorization = `Bearer ${currentToken}`;
              }
            }
          } else {
            // 기존 토큰 사용
            config.headers.Authorization = `Bearer ${currentToken}`;
          }
        } else {
          // 기존 토큰 사용
          const adminData = localStorage.getItem("admin");
          let token = null;
          if (adminData) {
            try {
              if (adminData.startsWith("eyJ")) {
                // 토큰 문자열만 저장된 경우
                token = adminData;
              } else {
                // 객체(JSON)로 저장된 경우
                token = JSON.parse(adminData).accessToken;
              }
            } catch (e) {
              // fallback: 그냥 문자열로 사용
              token = adminData;
            }
          }
          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
          }
        }
      }
    } catch (error) {
      console.error(
        "Failed to refresh token in apiClient request interceptor:",
        error
      );
      // 토큰 갱신 실패 시 기존 토큰 사용
      const adminData = localStorage.getItem("admin");
      let token = null;
      if (adminData) {
        try {
          if (adminData.startsWith("eyJ")) {
            token = adminData;
          } else {
            token = JSON.parse(adminData).accessToken;
          }
        } catch (e) {
          token = adminData;
        }
      }
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }

    return config;
  },
  (error) => {
    // 로딩 상태 감소
    activeRequests = Math.max(0, activeRequests - 1);
    console.log(
      "📤 API Client Request Error:",
      error.config?.url,
      "activeRequests:",
      activeRequests
    );
    if (activeRequests === 0) {
      notifyLoadingChange(false);
    }
    return Promise.reject(error);
  }
);

// 응답 인터셉터
apiClient.interceptors.response.use(
  (response: AxiosResponse<ApiResponse>) => {
    // 로딩 상태 감소
    activeRequests = Math.max(0, activeRequests - 1);

    if (activeRequests === 0) {
      notifyLoadingChange(false);
    }

    // result_data에 content가 있으면 최상위로 평면화
    if (response.data?.result_data?.content) {
      response.data = {
        ...response.data,
        content: response.data.result_data.content,
        totalPages: response.data.result_data.totalPages,
        totalElements: response.data.result_data.totalElements,
        page: response.data.result_data.page,
        size: response.data.result_data.size,
      };
    }

    // API 요청 성공 시 /admins/me 체크
    // - /auth/* : 로그인/리프레시 등 인증 요청은 제외 (로그인 오류 방지)
    // - admins/me : 자기 자신은 제외 (무한루프 방지)
    const url = response.config?.url || "";
    if (
      rootStore.isSignedIn &&
      !url.includes("/auth/") &&
      !url.includes("admins/me")
    ) {
      // _skipAuthRedirect: true — 배경 호출이 401/403 받아도 강제 로그아웃 방지
      apiClient.get("admins/me", { headers: { _skipAuthRedirect: "true" } }).then((res) => {
        rootStore.setProfile(res.data);
      }).catch(() => {});
    }

    return response;
  },
  async (error) => {
    // 로딩 상태 감소
    activeRequests = Math.max(0, activeRequests - 1);

    if (activeRequests === 0) {
      notifyLoadingChange(false);
    }

    // 배경 /admins/me 호출은 401/403 처리 건너뜀 (의도치 않은 로그아웃 방지)
    if (error.config?.headers?._skipAuthRedirect === "true") {
      return Promise.reject(error);
    }

    // 요청 실패(5xx 등)여도 /admins/me 체크
    const errorUrl = error.config?.url || "";
    if (
      rootStore.isSignedIn &&
      !errorUrl.includes("/auth/") &&
      !errorUrl.includes("admins/me")
    ) {
      apiClient.get("admins/me", { headers: { _skipAuthRedirect: "true" } }).then((res) => {
        rootStore.setProfile(res.data);
      }).catch(() => {});
    }

    // 401 에러 처리
    if (error.response?.status === 401) {
      try {
        // 토큰 갱신 시도 (리셋하지 않음 - MAX_REFRESH_ATTEMPTS 가드 유지)
        const newToken = await refreshAccessToken();
        // 원래 요청 재시도
        const originalRequest = error.config;
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return apiClient(originalRequest);
      } catch (refreshError) {
        // 토큰 갱신 실패 시 로그아웃
        console.error("Token refresh failed on 401 error:", refreshError);

        // 모든 토큰 관련 데이터 정리
        localStorage.removeItem("admin");
        localStorage.removeItem("user_info");
        localStorage.removeItem("refresh_token");
        localStorage.removeItem("signup_info");
        localStorage.removeItem("corp_info");

        // MobX store도 정리
        rootStore.setToken(null);
        rootStore.me = null;

        // 로그인 페이지로 리다이렉트
        if (window.location.pathname !== "/login") {
          window.location.href = "/login";
        }
      }
    }
    return Promise.reject(error);
  }
);

// API 메서드들 - 응답 데이터만 반환
export const api = {
  get: async <T = any>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<ApiResponse<T>> => {
    const response = await apiClient.get<ApiResponse<T>>(url, config);
    return response.data;
  },

  post: async <T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<ApiResponse<T>> => {
    const response = await apiClient.post<ApiResponse<T>>(url, data, config);
    return response.data;
  },

  put: async <T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<ApiResponse<T>> => {
    const response = await apiClient.put<ApiResponse<T>>(url, data, config);
    return response.data;
  },

  delete: async <T = any>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<ApiResponse<T>> => {
    const response = await apiClient.delete<ApiResponse<T>>(url, config);
    return response.data;
  },

  patch: async <T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<ApiResponse<T>> => {
    const response = await apiClient.patch<ApiResponse<T>>(url, data, config);
    return response.data;
  },
};

export default apiClient;
